import React from 'react';
import '../../../styles/components/step-indicator.css';

interface Step {
  title: string;
  description: string;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  compact?: boolean;
  onStepClick?: (stepIndex: number) => void;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep, compact = false, onStepClick }) => {
  const isStepActive = (index: number) => index === currentStep;
  const isStepCompleted = (index: number) => index < currentStep;
  const isStepClickable = (index: number) => isStepCompleted(index) && onStepClick;

  const handleStepClick = (index: number) => {
    if (isStepClickable(index) && onStepClick) {
      onStepClick(index);
    }
  };

  return (
    <div className={`step-indicator ${compact ? 'step-indicator--compact' : ''}`} data-step={currentStep}>
      {steps.map((step, index) => (
        <div
          key={index}
          className={`step-item ${isStepActive(index) ? 'active' : ''} ${
            isStepCompleted(index) ? 'completed' : ''
          } ${isStepClickable(index) ? 'clickable' : ''}`}
          aria-current={isStepActive(index) ? 'step' : undefined}
          aria-label={`Step ${index + 1}: ${step.title}`}
          onClick={() => handleStepClick(index)}
          role={isStepClickable(index) ? 'button' : undefined}
          tabIndex={isStepClickable(index) ? 0 : undefined}
          onKeyDown={(e) => {
            if (isStepClickable(index) && (e.key === 'Enter' || e.key === ' ')) {
              handleStepClick(index);
            }
          }}
        >
          <div className="step-circle">
            <span className="step-number" aria-hidden="true">{index + 1}</span>
          </div>
          {!compact && (
            <div className="step-content">
              <div className="step-label">{step.title}</div>
              {step.description && (
                <div className="step-description">{step.description}</div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default StepIndicator; 